/**
 * The year-on-year bridge (round-4 G1-N2): the score to date at last year-end, this year's change by the same legs the waterfall draws, and the score to date now.
 * The waterfall's bars are each counted from zero and add to the score to date; this adds last year's score to this year's change. All figures are printed cents, so the rows add to the printed score exactly.
 */
import type { RoundReport } from "@bsim/contracts";
import { formatUsdPerShare } from "../format";
import { scoreLegs, printed, type ScoreLeg } from "./decomposition";

export interface YearBridgeRow { readonly key: ScoreLeg["key"]; readonly label: string; readonly delta: number }
export interface YearBridge {
  /** Last year-end's score to date, as printed. */
  readonly prior: number;
  /** This year's change by cause; the rows add to `now - prior`. */
  readonly rows: readonly YearBridgeRow[];
  /** This year-end's score to date, as printed. */
  readonly now: number;
}

export const BRIDGE_LABEL: Readonly<Record<ScoreLeg["key"], string>> = {
  preview: "Your decisions this year, as previewed",
  path: "The market off the forecast",
  cardsArrived: "Cards that arrived",
  cardAnswers: "Your card answers",
  carried: "Earlier years, re-counted",
  interaction: "Left over once each step is counted alone",
};
export const BRIDGE_FOOTNOTE = "Last year's score was a projection that counted two look-through years; it is re-struck every year, so the re-counted line includes that.";

const cents = (v: number): number => Math.round(printed("valueCreated")(v) * 100);

/** Null in the first year, without last year's report, or when the report lacks the legs the waterfall is built from. */
export function yearBridge(report: RoundReport, previous: RoundReport | null | undefined): YearBridge | null {
  if (report.roundNo < 2 || previous == null || previous.roundNo !== report.roundNo - 1) return null;
  const legs = scoreLegs(report);
  if (legs === null) return null;
  const prior = cents(previous.score.valueCreatedUsdPerShare);
  const now = cents(legs.score);
  // The named legs as the waterfall prints them; the balancing line (the score less last year's, less the named legs) takes the cent the rounding leaves.
  const named = legs.legs.filter((l) => l.key !== "carried");
  const namedCents = named.map((l) => cents(l.delta));
  const balance = now - prior - namedCents.reduce((t, c) => t + c, 0);
  const rows: YearBridgeRow[] = [
    ...named.map((l, i): YearBridgeRow => ({ key: l.key, label: BRIDGE_LABEL[l.key], delta: (namedCents[i] ?? 0) / 100 })),
    { key: "carried", label: BRIDGE_LABEL.carried, delta: balance / 100 },
  ];
  return { prior: prior / 100, rows, now: now / 100 };
}

/** "Last year you ended at −$3.08 to date; this year moved you −$0.12, so you are at −$3.20 to date." */
export function yearBridgeSentence(b: YearBridge): string {
  const move = Math.round((b.now - b.prior) * 100) / 100;
  const level = (v: number): string => formatUsdPerShare(v, { signed: true });
  return `Last year you ended at ${level(b.prior)} to date; this year ${move === 0 ? "did not move you" : `moved you ${level(move)}`}, so you are at ${level(b.now)} to date.`;
}
